import produce from 'immer';
import isNil from 'lodash/isNil';
import differenceWith from 'lodash/differenceWith';

import {
  ChartType,
  DATASET_MIXPANEL,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  ValueRepresentationType,
  XAU_ONLY_MATH_TYPES,
} from 'common/report/constants';
import {
  ChartPlotStyle,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsResourceType,
} from 'common/report/insights/util/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isLiftEnabled} from 'common/report/util/lift';
import {LiftType} from 'common/report/util/lift/types';
import {Section, TimeSection} from 'common/report/insights/models/section';
import {CommonDataTableFields} from 'common/components/data-table/types';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';

import {DEFAULT_STEPS_TABLE_COLUMNS} from '../visualization/constants';
import {getPropertyFiltersFromBookmark} from './filters';
import {getBookmarkTimeClauseForDateRange, getDateRange} from './time';
import {fillMetricColumnSort} from './';

function getDisplayOptionOverridesForChartType(chartType: ChartType): Pick<DisplayOptions, 'chartType' | 'plotStyle'> {
  switch (chartType) {
    case ChartType.FunnelSteps:
      return {
        chartType: ChartType.FunnelSteps,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.RetentionCurve:
      return {
        chartType: ChartType.RetentionCurve,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Bar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedBar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Line:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedLine:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Pie:
      return {
        chartType: ChartType.Pie,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Table:
      return {
        chartType: ChartType.Table,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Metric:
      return {
        chartType: ChartType.Metric,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.FunnelTopPaths:
      return {
        chartType: ChartType.FunnelTopPaths,
        plotStyle: ChartPlotStyle.Standard,
      };
    default: {
      throw new Error(`unknown chart type "${chartType}"`);
    }
  }
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithChartType(
  bookmarkParams: InsightsBookmarkParams,
  chartTypeOverride: ChartType,
): InsightsBookmarkParams {
  const {displayOptions} = bookmarkParams;
  const {funnelStepsSelectedTableColumns, ...restDisplayOptions} = displayOptions;
  return {
    ...bookmarkParams,
    displayOptions: {
      ...restDisplayOptions,
      ...getDisplayOptionOverridesForChartType(chartTypeOverride),
      ...(chartTypeOverride === ChartType.FunnelSteps
        ? {funnelStepsSelectedTableColumns: DEFAULT_STEPS_TABLE_COLUMNS}
        : {}),
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithDateRange(
  bookmarkParams: InsightsBookmarkParams,
  dateRangeOverride: DateRange,
  timeUnitOverride?: TimeUnit,
): InsightsBookmarkParams {
  const currentTimeSection = Section.deserialize(TimeSection.TYPE, bookmarkParams.sections?.time);
  const currentDateRange = getDateRange(currentTimeSection.clauses);

  if (currentDateRange.equals(dateRangeOverride)) {
    return bookmarkParams;
  }

  const newTimeClause = getBookmarkTimeClauseForDateRange(dateRangeOverride);
  if (timeUnitOverride) {
    newTimeClause.unit = timeUnitOverride;
  }
  const showClauses = bookmarkParams.sections?.show;
  // As of writing, arb doesn't support Cohorts over Time queries with anything finer than Day for perf reasons
  const hasCohortsOverTime = getBehaviorShowClauses(showClauses).some(
    (showClause) =>
      showClause.behavior.resourceType === InsightsResourceType.Cohorts ||
      showClause.behavior.resourceType === InsightsResourceType.People,
  );
  const invalidTimeUnits: TimeUnit[] = [TimeUnit.Hour, TimeUnit.Minute];
  if (hasCohortsOverTime && invalidTimeUnits.includes(newTimeClause.unit)) {
    newTimeClause.unit = TimeUnit.Day;
  }
  const hasXauClause = getBehaviorShowClauses(showClauses).some((clause) =>
    (XAU_ONLY_MATH_TYPES as MATH_TYPE[]).includes(clause.measurement.math),
  );
  if (hasXauClause && invalidTimeUnits.includes(newTimeClause.unit)) {
    newTimeClause.unit = TimeUnit.Day;
  }
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      time: [newTimeClause],
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: InsightsBookmarkParams,
  filters: PropertyFilter[],
): InsightsBookmarkParams {
  const currentFilters = getPropertyFiltersFromBookmark(bookmarkParams.sections.filter);

  // Don't add duplicate filters
  const filtersToAppend = differenceWith(filters, currentFilters, (f1, f2) => f1.equals(f2));
  const insightsFilters: TODO[] = filtersToAppend.map((propertyFilter) => {
    return insightsFilterAttrsFromPropertyFilter({dataset: DATASET_MIXPANEL, ...propertyFilter});
  });

  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      filter: [...bookmarkParams.sections.filter, ...insightsFilters],
    },
  };
}

/**
 * Converts relative value display to the appropriate LiftComparison
 * and sets value display to absolute, ONLY if the Lift feature flag is enabled for the project.
 *
 * @param {InsightsBookmarkParams} bookmarkParams: The parameters needing a check/update.
 * @returns {InsightsBookmarkParams} a copy of the passed-in params, with the update made.
 */
export function getBookmarkParamsWithRelativeValueUpdated(
  bookmarkParams: InsightsBookmarkParams,
): InsightsBookmarkParams {
  // It's important that these override methods return a whole bookmark params
  // so that it does not require additional knowledge of the bookmark format to use effectively

  // value representation check repeated here, to avoid cloning params we won't change.
  if (isLiftEnabled() && bookmarkParams.displayOptions?.value === ValueRepresentationType.Relative) {
    const newParams = {
      ...bookmarkParams,
    };

    updateRelativeDisplayToLiftComparison(newParams);
    return newParams;
  } else {
    return bookmarkParams;
  }
}

/**
 * Updates a partial InsightsBookmarkParams to have an "Overall" LiftComparison, if the `displayOptions.value` is Relative.
 *
 * WARNING: Modifies the params in-place. Only pass in cloned params.
 * Only call this if Lift is enabled for a project.
 *
 * Use this to convert params on-the-fly, until we do a bookmark migration for the Lift feature.
 *
 * @param {Partial<InsightsBookmarkParams>} bookmarkParams Must have `displayOptions` for this method to do anything.
 */
export function updateRelativeDisplayToLiftComparison(bookmarkParams: Partial<InsightsBookmarkParams>) {
  if (bookmarkParams.displayOptions?.value === ValueRepresentationType.Relative) {
    // presume that if they already have a liftComparison, it's what the user wants
    if (!bookmarkParams.liftComparison) {
      bookmarkParams.liftComparison = {
        type: LiftType.Overall,
      };
    }
    bookmarkParams.displayOptions.value = ValueRepresentationType.Absolute;
  }
}

/**
 * This transformation of sorting params is needed to populate missing sort fields for the "sort by column" mode
 * historically certain chart types have omitted rendering the metric column for single segmented metric reports
 * whenever a report is loaded with that configuration, we want to maintain any previous sorting state after
 * injecting the metric column into the results
 */
export function updateSortConfig(
  params: InsightsBookmarkParams,
  {suppressMigrationAlert = true}: {suppressMigrationAlert?: boolean} = {},
) {
  if (isNil(params.sorting)) {
    return params;
  }

  const groupByCount = (params.sections?.cohorts?.length ? 1 : 0) + params.sections?.group?.length ?? 0;
  const hasHistogram = getBehaviorShowClauses(params.sections?.show || []).some(
    (clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM,
  );
  const headerCount = 1 + groupByCount + (hasHistogram ? 1 : 0);

  const defaultSortValue = {
    [ChartType.Line]: CommonDataTableFields.Average,
    [ChartType.Pie]: CommonDataTableFields.Total,
    [ChartType.Metric]: CommonDataTableFields.Total,
  };

  return produce(params, (draft) => {
    for (const type of [ChartType.Line, ChartType.Pie, ChartType.Metric]) {
      if (draft.sorting[type]) {
        draft.sorting[type] = fillMetricColumnSort(
          type,
          headerCount,
          draft.sorting[type],
          defaultSortValue[type],
          suppressMigrationAlert,
        );
      }
    }
  });
}
